import React, { Suspense } from 'react';
import { Navigate, Routes, Route, Outlet } from 'react-router-dom';
import { SessionGate } from '@/components/common/SessionGate';
import ProtectedRoute from '@/components/common/ProtectedRoute';

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import('@/pages/login/LoginPage'));
const GuideIndexPage = React.lazy(() => import('@/pages/guides/GuideIndexPage'));
const ChangePasswordPage = React.lazy(() => import('@/pages/auth/ChangePasswordPage'));
const DashboardPage = React.lazy(() => import('@/pages/dashboard/DashboardPage'));
const SupplierListPage = React.lazy(() => import('@/pages/suppliers/SupplierListPage'));
const CustomerListPage = React.lazy(() => import('@/pages/customers/CustomerListPage'));
const PriceListPage = React.lazy(() => import('@/pages/pricing/PriceListPage'));
const CustomerGroupPricingPage = React.lazy(() => import('@/pages/pricing/CustomerGroupPricingPage'));
const ServiceListPage = React.lazy(() => import('@/pages/services/ServiceListPage'));
const ServiceLinesPage = React.lazy(() => import('@/pages/ServiceLines'));
const CategoryListPage = React.lazy(() => import('@/pages/categories/CategoryListPage'));
const CategoryHierarchyPage = React.lazy(() => import('@/pages/categories/CategoryHierarchyPage'));
const MaterialListPage = React.lazy(() => import('@/pages/materials/MaterialListPage'));
const CuttingOrderListPage = React.lazy(() => import('@/pages/cutting/CuttingOrderListPage'));
const CuttingConfigListPage = React.lazy(() => import('@/pages/cuttingConfigs/CuttingConfigListPage'));
const CuttingSizeListPage = React.lazy(() => import('@/pages/cuttingSizes/CuttingSizeListPage'));
const OrderListPage = React.lazy(() => import('@/pages/orders/OrderListPage'));
const CreateOrderPage = React.lazy(() => import('@/pages/orders/CreateOrderPage'));
// const LinkedOrdersPage = React.lazy(() => import('@/pages/orders/LinkedOrdersPage'));
const InvoiceListPage = React.lazy(() => import('@/pages/invoices/InvoiceListPage'));
const ReceiptVoucherListPage = React.lazy(() => import('@/pages/receiptVouchers/ReceiptVoucherListPage'));
const DiscountVoucherListPage = React.lazy(() => import('@/pages/discountVouchers/DiscountVoucherListPage'));
const PaymentVouchersPage = React.lazy(() => import('@/pages/PaymentVouchersPage'));
const CashBookPage = React.lazy(() => import('@/pages/cashBook/CashBookPage'));
const WarehouseReceiptsPage = React.lazy(() => import('@/pages/warehouseReceipts'));
const WarehouseReceiptRequestsPage = React.lazy(() => import('@/pages/warehouseReceiptRequests'));
const WarehouseDeliveriesPage = React.lazy(() => import('@/pages/warehouseDeliveries'));
const WarehouseChecksPage = React.lazy(() => import('@/pages/warehouseChecks'));
const UserProfilePage = React.lazy(() => import('@/pages/profile/UserProfilePage'));
const RoleListPage = React.lazy(() => import('@/pages/roles/RoleListPage'));
const AuthAuditPage = React.lazy(() => import('@/pages/users/AuthAuditPage'));
const UserListPage = React.lazy(() => import('@/pages/users/UserListPage'));
const WorkPoolPage = React.lazy(() => import('@/pages/work-pool/WorkPoolPage'));
const SupplierReportPage = React.lazy(() => import('@/pages/reports/SupplierReportPage'));
const CustomerReportPage = React.lazy(() => import('@/pages/reports/CustomerReportPage'));
const ServiceReportPage = React.lazy(() => import('@/pages/reports/ServiceReportPage'));
const ServiceInvoiceDetailPage = React.lazy(() => import('@/pages/reports/ServiceInvoiceDetailPage'));
const ProductionErrorReportPage = React.lazy(() => import('@/pages/reports/ProductionErrorReportPage'));
const SalesReportPage = React.lazy(() => import('@/pages/reports/SalesReportPage'));
const InventoryReportPage = React.lazy(() => import('@/pages/reports/InventoryReportPage'));
const NotificationsPage = React.lazy(() => import('@/pages/notifications/NotificationsPage'));
const DailyDeliveryListPage = React.lazy(() => import('@/pages/delivery/DailyDeliveryListPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Catch all component for 404 errors
const CatchAll = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-gray-600 mb-4">Page not found</p>
      <a href="/dashboard" className="btn-primary">Go Home</a>
    </div>
  </div>
);

export const AppRouter: React.FC = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/guides" element={<GuideIndexPage />} />
      <Route path="/auth/change-password" element={<SessionGate allowPasswordChange><ChangePasswordPage /></SessionGate>} />
      <Route element={<SessionGate><ProtectedRoute><Outlet /></ProtectedRoute></SessionGate>}>
        <Route path="/" element={<Navigate replace to="/dashboard" />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/suppliers" element={<SupplierListPage />} />
        <Route path="/customers" element={<CustomerListPage />} />
        <Route path="/pricing" element={<PriceListPage />} />
        <Route path="/pricing/customer-groups" element={<CustomerGroupPricingPage />} />
        <Route path="/services" element={<ServiceListPage />} />
        <Route path="/service-lines" element={<ServiceLinesPage />} />
        <Route path="/categories" element={<CategoryListPage />} />
        <Route path="/categories/hierarchy" element={<CategoryHierarchyPage />} />
        <Route path="/materials" element={<MaterialListPage />} />
        <Route path="/cutting" element={<CuttingOrderListPage />} />
        <Route path="/cutting-configs" element={<CuttingConfigListPage />} />
        <Route path="/cutting-sizes" element={<CuttingSizeListPage />} />
        <Route path="/work-pool" element={<WorkPoolPage />} />
        <Route path="/delivery/daily" element={<DailyDeliveryListPage />} />
        <Route path="/orders" element={<OrderListPage />} />
        <Route path="/orders/create" element={<CreateOrderPage />} />
        <Route path="/invoices" element={<InvoiceListPage />} />
        <Route path="/receipt-vouchers" element={<ReceiptVoucherListPage />} />
        <Route path="/discount-vouchers" element={<DiscountVoucherListPage />} />
        <Route path="/payment-vouchers" element={<PaymentVouchersPage />} />
        <Route path="/cash-book" element={<CashBookPage />} />
        <Route path="/warehouse-receipts" element={<WarehouseReceiptsPage />} />
        <Route path="/warehouse-receipt-requests" element={<WarehouseReceiptRequestsPage />} />
        <Route path="/warehouse-deliveries" element={<WarehouseDeliveriesPage />} />
        <Route path="/warehouse-checks" element={<WarehouseChecksPage />} />
        <Route path="/profile" element={<UserProfilePage />} />
        <Route path="/roles" element={<RoleListPage />} />
        <Route path="/auth-audit" element={<AuthAuditPage />} />
        <Route path="/users" element={<UserListPage />} />
        <Route path="/reports/suppliers" element={<SupplierReportPage />} />
        <Route path="/reports/customers" element={<CustomerReportPage />} />
        <Route path="/reports/services" element={<ServiceReportPage />} />
        <Route path="/reports/services/:serviceCode/invoices" element={<ServiceInvoiceDetailPage />} />
        <Route path="/reports/production-errors" element={<ProductionErrorReportPage />} />
        <Route path="/reports/sales" element={<SalesReportPage />} />
        <Route path="/reports/inventory" element={<InventoryReportPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
      </Route>
      <Route path="*" element={<SessionGate><CatchAll /></SessionGate>} />
    </Routes>
  </Suspense>
);
